/**
 * Server state for My Files (HS-1441).
 *
 * **The only file in this feature that names the transport.** Since HS-1484 that is the generated
 * Admin API client throughout — the reads, the ticket, the two deletes and the upload — plus one
 * hand-written `fetch` for the download's bytes, whose route streams a file and so declares no
 * response schema to generate against.
 *
 * Nothing else in the feature moved with it: not the adapter, not a component, not a test of
 * either. That was the point of the split (HS-1441) and it held.
 *
 * Every failure a component has to word arrives as a {@link FilesApiError} carrying the envelope's
 * `errorCode`, so the copy is chosen from a key and never from a message. The contract document
 * called that member `reason`; the shipped API calls it `errorCode` and sends the same values.
 */
import {
  deleteApiPortalMeFilesFoldersId,
  deleteApiPortalMeFilesId,
  getApiPortalMeFiles,
  getApiPortalMeFilesCounts,
  getApiPortalMeFilesFolders,
  getApiPortalMeFilesId,
  getApiPortalMeFilesIdDownload,
  getApiPortalMeFilesOrigins,
  getApiPortalMeFilesStorage,
  NormalizedApiError,
  patchApiPortalMeFilesFoldersId,
  postApiPortalMeFiles,
  postApiPortalMeFilesDelete,
  postApiPortalMeFilesDownload,
  postApiPortalMeFilesFolders,
  postApiPortalMeFilesFoldersIdMove,
  postApiPortalMeFilesMove,
  type GetApiPortalMeFilesParams,
  type PortalBulkDeleteDto,
  type PortalFileCountsDto,
  type PortalFileFolderDto,
  type PortalFileListDto,
  type PortalFileListItemDto,
  type PortalFileLocation,
  type PortalFileOriginsDto,
  type PortalFileSelectionRequest,
} from '@smartsash/api-client';
import { queryOptions } from '@tanstack/react-query';

import {
  toDeleteResults,
  toDownloadTicket,
  toFileCounts,
  toFileRow,
  toFolder,
  toMoveResults,
  toFilesView,
  toSecureTicketUrl,
  toStorageView,
} from './files.adapter';
import type {
  CloudProviderDto,
  DeleteResult,
  DownloadTicket,
  FileCounts,
  FileCountsDto,
  FileErrorReasonDto,
  FileListDto,
  FileListItemDto,
  FileListParams,
  FileLocation,
  FileOriginsDto,
  FileRow,
  FileSelectionDto,
  FilesView,
  Folder,
  FolderDto,
  MoveResult,
  StorageView,
  TransferHooks,
  UploadParams,
} from './files.types';
import { downloadDebugOutcome } from './filesDebug';

import { env } from '@/env';

/**
 * An `errorCode` the portal has copy for, or `unknown` — never the raw string a backend sent.
 *
 * `notFound` and `forbidden` are not wire codes: they are read off the status, because the API
 * states those two by status alone. The distinction matters and is deliberate on the backend's
 * part — a file that is not the caller's is **404, never 403**, malformed ids included, so a 403
 * means only one thing: the role does not hold `File.Upload` or `File.Delete`.
 */
export type FilesApiReason = FileErrorReasonDto | 'notFound' | 'forbidden' | 'unknown';

/** Every code the API can put in the envelope's `errorCode`, plus the two read off the status. */
const KNOWN_REASONS: FilesApiReason[] = [
  'legalHold',
  'providerFailed',
  'providerUnreachable',
  'providerNotConnected',
  'disabledByInstitution',
  'quotaExceeded',
  'tooLarge',
  'typeRejected',
  'quarantined',
  'selectionTooLarge',
  'folderNameTaken',
  'folderNotEmpty',
  'folderCycle',
  'folderDepthExceeded',
  'folderLimitReached',
  'notFound',
  'forbidden',
];

const HTTP_FORBIDDEN = 403;
const HTTP_NOT_FOUND = 404;
const HTTP_PAYLOAD_TOO_LARGE = 413;
const HTTP_SERVER_ERROR = 500;

/**
 * The upload route refuses a request without this header (HS-1483): a multipart POST is a CORS
 * simple request, and the custom header is what forces the preflight that keeps another site from
 * uploading into the user's storage. The OpenAPI document does not declare it, so the generated
 * call cannot send it on its own (HS-1532).
 */
const REQUESTED_WITH_HEADER = 'X-Requested-With';
const REQUESTED_WITH_VALUE = 'XMLHttpRequest';

/**
 * What a component gets when a write or a ticket read fails.
 *
 * Declared here, in permanent code, rather than letting components catch the transport's own
 * error. That prediction held exactly: when the mock was replaced, {@link toApiError} was the only
 * thing that changed, and every component that words a failure was untouched.
 */
export class FilesApiError extends Error {
  constructor(
    readonly status: number,
    readonly reason: FilesApiReason,
    readonly provider: FileLocation | null = null,
  ) {
    super(`Files API ${String(status)} (${reason})`);
    this.name = 'FilesApiError';
  }
}

/** The user cancelled a transfer — not a failure, and never shown as one. */
export class TransferCancelledError extends Error {
  constructor() {
    super('Transfer cancelled');
    this.name = 'TransferCancelledError';
  }
}

function toReason(value: string | undefined): FilesApiReason | undefined {
  return KNOWN_REASONS.find((reason) => reason === value);
}

/**
 * The reason a failed request carries, from its `errorCode` first and its status second.
 *
 * The status fallback is not a nicety. A body larger than roughly 501 MB is refused by the server
 * framework *before* our handler runs, so it answers a **bare 413 with no `errorCode`** — read
 * from the code alone that is `unknown`, and the user is told "try again in a moment" about a file
 * that will never fit.
 */
function reasonFor(status: number, errorCode: string | undefined): FilesApiReason {
  const fromCode = toReason(errorCode);
  if (fromCode !== undefined) return fromCode;
  if (status === HTTP_FORBIDDEN) return 'forbidden';
  if (status === HTTP_NOT_FOUND) return 'notFound';
  if (status === HTTP_PAYLOAD_TOO_LARGE) return 'tooLarge';
  return 'unknown';
}

/**
 * Whether a thrown value is the caller's own abort rather than a failure.
 *
 * Two transports, two spellings, and one of them arrives wrapped. `fetch` rejects with a
 * `DOMException` named `AbortError`; axios rejects with its `CanceledError` (`ERR_CANCELED`), and
 * `customInstance` has already normalised that into a `NormalizedApiError` carrying no status —
 * indistinguishable from a network failure unless the `cause` is unwrapped. Both are checked,
 * because the download toast draws "Canceled" and "Failed" as different states and the user chose
 * one of them.
 */
function isAbort(error: unknown): boolean {
  const named = (value: unknown): boolean =>
    typeof value === 'object' &&
    value !== null &&
    ((value as { name?: unknown }).name === 'AbortError' ||
      (value as { name?: unknown }).name === 'CanceledError' ||
      (value as { code?: unknown }).code === 'ERR_CANCELED');
  return named(error) || named((error as { cause?: unknown } | null)?.cause);
}

/** Translate whatever the transport threw into the two errors the feature knows about. */
export function toApiError(error: unknown): Error {
  if (error instanceof FilesApiError || error instanceof TransferCancelledError) return error;
  if (isAbort(error)) return new TransferCancelledError();
  if (error instanceof NormalizedApiError) {
    // The provider is always null on this path: the API sends no provider with an error, and every
    // upload goes to `hawkins` until Cloud Storage has a backend. The field stays because the
    // wording interpolates it the day it does.
    return new FilesApiError(
      error.status ?? HTTP_SERVER_ERROR,
      reasonFor(error.status ?? HTTP_SERVER_ERROR, error.errorCode),
      null,
    );
  }
  return error instanceof Error ? error : new Error(String(error));
}

/** Whether a thrown value is a 403: the role lacks `File.Upload` or `File.Delete`. */
export function isForbidden(error: unknown): boolean {
  return isFilesApiError(error) && error.status === HTTP_FORBIDDEN;
}

async function guarded<T>(read: () => Promise<T>): Promise<T> {
  try {
    return await read();
  } catch (error) {
    throw toApiError(error);
  }
}

/* ------------------------------------------------------------------ *
 * The generated boundary (HS-1484)
 *
 * The generated DTOs and ours disagree in two places, and both disagreements are the API's, not
 * a mistake in either model.
 *
 * **Absence.** `id`, `name`, `extension` and `mimeType` are declared `required` *and* `nullable`,
 * so the generated types are `string | null` where ours say `string`. The adapter already coerces
 * three of them; `id` it does not, because a row without one cannot be linked to, downloaded or
 * deleted — there is nothing to render, only something to drop.
 *
 * **Spelling.** Every query parameter generates as `string`, the repeatable ones as `string[]`.
 * The wire was always right — `serializeParams` in `customInstance` stringifies numbers and
 * booleans and spells arrays as repeated keys — so this is a type-boundary translation and not a
 * behaviour change.
 * ------------------------------------------------------------------ */

/** `undefined` for an absent value, so an optional parameter is absent rather than empty. */
function text(value: string | null | undefined): string | undefined {
  return value ?? undefined;
}

function num(value: number | undefined): string | undefined {
  return value === undefined ? undefined : String(value);
}

function flag(value: boolean | undefined): string | undefined {
  return value === true ? 'true' : undefined;
}

/**
 * Our params object as the endpoint's query string.
 *
 * Written out member by member rather than spread: **an unknown query parameter is a 400 here**,
 * deliberately, so that a typo fails loudly instead of quietly returning everything. A spread
 * would forward whatever a caller happened to put on the object.
 */
export function toListParams(params: FileListParams): GetApiPortalMeFilesParams {
  return {
    page: num(params.page),
    pageSize: num(params.pageSize),
    sort: params.sort,
    q: params.q,
    type: params.type,
    originLabId: params.originLabId,
    originNone: flag(params.originNone),
    located: params.located,
    from: params.from,
    to: params.to,
    sizeMinBytes: num(params.sizeMinBytes),
    sizeMaxBytes: num(params.sizeMaxBytes),
    status: params.status,
    folderId: params.folderId,
    folderNone: flag(params.folderNone),
  };
}

/** A notice's provider is one of the two cloud services; the API types it as any location. */
function toNoticeProvider(value: PortalFileLocation | null | undefined): CloudProviderDto {
  return value === 'googleDrive' ? 'googleDrive' : 'oneDrive';
}

function toItemDto(dto: PortalFileListItemDto): FileListItemDto | null {
  const id = text(dto.id);
  if (id === undefined || id === '') return null;
  return {
    id,
    name: dto.name ?? '',
    extension: dto.extension ?? '',
    mimeType: dto.mimeType ?? '',
    sizeBytes: dto.sizeBytes,
    createdAt: dto.createdAt,
    type: dto.type ?? null,
    origin:
      dto.origin == null
        ? null
        : {
            sessionId: dto.origin.sessionId ?? '',
            labId: dto.origin.labId ?? '',
            labName: dto.origin.labName ?? '',
          },
    located: dto.located,
    status: dto.status,
  };
}

function toListDto(dto: PortalFileListDto): FileListDto {
  return {
    items: (dto.items ?? [])
      .map(toItemDto)
      .filter((item): item is FileListItemDto => item !== null),
    page: dto.page,
    // The server clamps `pageSize` to 100 and echoes the clamp back. The pager reads what came
    // back, never what was asked for, so a clamped request still pages correctly.
    pageSize: dto.pageSize,
    totalCount: dto.totalCount,
    totalUnfiltered: dto.totalUnfiltered,
    notices: (dto.notices ?? []).map((notice) => ({
      kind: notice.kind,
      provider: toNoticeProvider(notice.provider),
      fileId: notice.fileId ?? null,
    })),
  };
}

/**
 * One folder of the tree, or `null` for a row without an id — like a file without one, there is
 * nothing to open, rename or move.
 */
function toFolderDto(dto: PortalFileFolderDto): FolderDto | null {
  const id = text(dto.id);
  if (id === undefined || id === '') return null;
  return {
    id,
    name: dto.name ?? '',
    parentFolderId: dto.parentFolderId ?? null,
    fileCount: dto.fileCount,
    createdAt: dto.createdAt,
  };
}

/** A folder write answers the folder as it now stands; one without an id is a server error. */
function toWrittenFolder(dto: PortalFileFolderDto): Folder {
  const folder = toFolderDto(dto);
  if (folder === null) throw new FilesApiError(HTTP_SERVER_ERROR, 'unknown');
  return toFolder(folder);
}

/**
 * A bulk-delete envelope as our results. Shared by the bulk delete and the recursive folder delete,
 * which answers the same shape — and, on an empty folder, may answer no body at all.
 */
function toDeleteResultList(dto: PortalBulkDeleteDto | undefined): DeleteResult[] {
  return toDeleteResults({
    results: (dto?.results ?? []).map((result) => ({
      id: result.id ?? '',
      name: result.name ?? '',
      outcome: result.outcome,
    })),
  });
}

/** Our selection as the endpoint's body. Exactly one member travels; the API rejects both. */
function toSelectionRequest(selection: FileSelectionDto): PortalFileSelectionRequest {
  if ('ids' in selection) return { ids: selection.ids };
  const {
    page: _page,
    pageSize: _pageSize,
    sort: _sort,
    ...filter
  } = toListParams(selection.filter);
  return { filter };
}

function toOriginsDto(dto: PortalFileOriginsDto): FileOriginsDto {
  return {
    labs: (dto.labs ?? []).map((lab) => ({ id: lab.id, name: lab.name ?? '' })),
    hasFilesWithoutSession: dto.hasFilesWithoutSession,
  };
}

/**
 * Cache keys.
 *
 * `root` is what every write invalidates — an upload or a delete moves both the list and the
 * meter, and a provider connect changes the rows' `located` and `status`.
 */
export const filesKeys = {
  root: ['portal', 'files'] as const,
  list: (params: FileListParams) => ['portal', 'files', 'list', params] as const,
  detail: (id: string) => ['portal', 'files', 'detail', id] as const,
  preview: (id: string) => ['portal', 'files', 'preview', id] as const,
  storage: ['portal', 'files', 'storage'] as const,
  origins: ['portal', 'files', 'origins'] as const,
  /** Under `root`, so every write that moves a file also refreshes the folders' counts. */
  folders: ['portal', 'files', 'folders'] as const,
  /** Under `root` for the same reason: an upload, a move or a delete changes the figures. */
  counts: ['portal', 'files', 'counts'] as const,
};

export function filesQuery(params: FileListParams) {
  return queryOptions<FilesView>({
    queryKey: filesKeys.list(params),
    queryFn: async () =>
      toFilesView(toListDto(await guarded(() => getApiPortalMeFiles(toListParams(params))))),
  });
}

/**
 * `GET …/{id}` — the one row the preview page is about; a 404 is {@link isNotFound}.
 *
 * A file that is not the caller's answers 404 rather than 403, and so does a malformed id, so the
 * not-found page is the single outcome for "this is not yours" and "this does not exist". That is
 * the backend's decision and the right one: a 403 here would confirm the file exists.
 */
export function fileQuery(id: string) {
  return queryOptions<FileRow>({
    queryKey: filesKeys.detail(id),
    queryFn: async () => {
      const dto = toItemDto(await guarded(() => getApiPortalMeFilesId(id)));
      // A row the list would drop cannot be a page either — there is no id to act on.
      if (dto === null) throw new FilesApiError(HTTP_NOT_FOUND, 'notFound');
      return toFileRow(dto);
    },
  });
}

/**
 * The file's bytes for the preview page: the same ticket and the same fetch as a download, so
 * there is one path for bytes and one set of failure states (HS-1441A). Held as a `Blob`; the
 * page decides whether to read it as text or hand it to the browser as an object URL.
 */
export function filePreviewQuery(id: string) {
  return queryOptions<Blob>({
    queryKey: filesKeys.preview(id),
    queryFn: async () => fetchDownload(await requestDownload({ ids: [id] }), {}),
    // The bytes of a stored file do not change under the user; a fresh ticket per mount is waste.
    staleTime: Infinity,
  });
}

export function storageQuery() {
  return queryOptions<StorageView>({
    queryKey: filesKeys.storage,
    queryFn: async () => toStorageView(await guarded(() => getApiPortalMeFilesStorage())),
  });
}

export function fileOriginsQuery() {
  return queryOptions<FileOriginsDto>({
    queryKey: filesKeys.origins,
    queryFn: async () => toOriginsDto(await guarded(() => getApiPortalMeFilesOrigins())),
  });
}

/**
 * `GET …/folders` — the whole tree, flat (HS-1724). Fetched once per screen; the hierarchy, the
 * breadcrumbs and the move picker are all derived from it by `folderTree.ts`.
 */
export function foldersQuery() {
  return queryOptions<Folder[]>({
    queryKey: filesKeys.folders,
    queryFn: async () => {
      const dto = await guarded(() => getApiPortalMeFilesFolders());
      return (dto.folders ?? [])
        .map(toFolderDto)
        .filter((folder): folder is FolderDto => folder !== null)
        .map(toFolder);
    },
  });
}

/** The generated counts as our DTO: an absent list is none, an entry without an id is dropped. */
function toCountsDto(dto: PortalFileCountsDto): FileCountsDto {
  return {
    root: dto.root,
    folders: (dto.folders ?? []).flatMap((entry) => {
      const id = text(entry.id);
      return id === undefined || id === '' ? [] : [{ ...entry, id }];
    }),
  };
}

/**
 * `GET …/counts` (HS-1763) — every figure the tree shows, in one request: the root's and each
 * folder's, direct and recursive. The list's own `totalCount` cannot stand in for these — it is
 * one folder's direct files — and summing the tree's direct counts would need every level loaded.
 */
export function countsQuery() {
  return queryOptions<FileCounts>({
    queryKey: filesKeys.counts,
    queryFn: async () =>
      toFileCounts(toCountsDto(await guarded(() => getApiPortalMeFilesCounts()))),
  });
}

/**
 * `POST …/folders` — a new folder under `parentFolderId`, or at the root for `null`.
 *
 * The 409s are the user's to act on and arrive as their own reasons: `folderNameTaken` (a sibling
 * has that name, compared case-insensitively), `folderDepthExceeded` (32 levels) and
 * `folderLimitReached` (500 folders). A parent deleted meanwhile is a 404.
 */
export async function createFolder(name: string, parentFolderId: string | null): Promise<Folder> {
  return toWrittenFolder(
    await guarded(() => postApiPortalMeFilesFolders({ name, parentFolderId })),
  );
}

/** `PATCH …/folders/{id}` — the name only. Renaming a folder to its own name is not a collision. */
export async function renameFolder(id: string, name: string): Promise<Folder> {
  return toWrittenFolder(await guarded(() => patchApiPortalMeFilesFoldersId(id, { name })));
}

/**
 * `POST …/folders/{id}/move` — a separate route from the rename because one `PATCH` cannot say
 * "to the root": the member is always sent, and `null` **is** the root.
 *
 * `folderCycle` is a move into the folder's own subtree, which the picker already leaves out; it
 * is still handled, because the tree the picker drew can be older than the server's.
 */
export async function moveFolder(id: string, parentFolderId: string | null): Promise<Folder> {
  return toWrittenFolder(
    await guarded(() => postApiPortalMeFilesFoldersIdMove(id, { parentFolderId })),
  );
}

/**
 * `DELETE …/folders/{id}` — with `recursive`, the folder **and everything in it**.
 *
 * The portal always sends `recursive=true`, after a confirmation that says so (sweep decision,
 * 2026-10-24). The answer is then the bulk delete's per-file outcomes, and the delete is not
 * atomic for the same reason: a `legalHold` file is kept and named, and so is every folder on its
 * way up, rather than vanishing with its parent. An empty folder may answer no body, which reads as
 * no results. Without `recursive` a non-empty folder is `409 folderNotEmpty`.
 */
export async function deleteFolder(id: string, recursive = true): Promise<DeleteResult[]> {
  const dto = await guarded(() =>
    deleteApiPortalMeFilesFoldersId(id, recursive ? { recursive: 'true' } : undefined),
  );
  // The generated type is `PortalBulkDeleteDto | void`: the 204 of an empty folder has no body.
  return toDeleteResultList(typeof dto === 'object' ? dto : undefined);
}

/**
 * `POST …/files/move` — files by id into `folderId`, or to the root for `null`.
 *
 * Ids only, at most 500 (`409 selectionTooLarge`): unlike download and delete there is no `filter`
 * form, so a "select all N" cannot be moved. Always 200 otherwise, with an outcome per file; a
 * destination that is not the caller's fails every file as `folderNotFound` and moves nothing.
 */
export async function moveFiles(ids: string[], folderId: string | null): Promise<MoveResult[]> {
  const dto = await guarded(() => postApiPortalMeFilesMove({ ids, folderId }));
  return toMoveResults({
    results: (dto.results ?? []).map((result) => ({
      id: result.id ?? '',
      name: result.name ?? '',
      outcome: result.outcome,
    })),
  });
}

/**
 * `GET …/{id}/download` or `POST …/download` — the ticket, not the bytes.
 *
 * One file takes the GET route it is documented under; anything else posts the selection. More
 * than one file comes back as **one zip, one URL, one toast**, so the caller's shape does not
 * change either way.
 *
 * A `filter` selection is resolved **when this runs**, not when the list was drawn: a file
 * uploaded in between is in the archive. That is the API's rule and the reason the client never
 * enumerates a "select all" itself.
 */
export async function requestDownload(selection: FileSelectionDto): Promise<DownloadTicket> {
  const single = 'ids' in selection && selection.ids.length === 1 ? selection.ids[0] : undefined;
  const dto = await guarded(() =>
    single === undefined
      ? postApiPortalMeFilesDownload(toSelectionRequest(selection))
      : getApiPortalMeFilesIdDownload(single),
  );
  return toDownloadTicket({
    url: toSecureTicketUrl(dto.url ?? '', env.VITE_API_BASE_URL),
    expiresAt: dto.expiresAt,
    fileName: dto.fileName ?? '',
    mimeType: dto.mimeType ?? '',
    sizeBytes: dto.sizeBytes,
  });
}

/**
 * The bytes behind a ticket, as a `Blob` the browser can save.
 *
 * Fetched directly rather than through the generated client: the byte route declares no response
 * schema — it streams the file — so orval types it `void`, and the progress bar needs the stream
 * anyway. The URL is on the API's own host and the token **is** the authorisation, so the request
 * is deliberately anonymous; sending the session cookie would add nothing and widen what a leaked
 * URL reaches.
 *
 * **Ranges are not supported** (`Accept-Ranges: none`), so an interrupted transfer restarts from
 * zero. There is no resume to build on top of this.
 */
export async function fetchDownload(ticket: DownloadTicket, hooks: TransferHooks): Promise<Blob> {
  const debug = downloadDebugOutcome(window.location.search);
  if (debug === 'hang') {
    // Never settles — except on abort, which the toast's own timeout or Cancel triggers.
    return new Promise<Blob>((_resolve, reject) => {
      hooks.signal?.addEventListener('abort', () => {
        reject(new TransferCancelledError());
      });
    });
  }
  if (debug === 'failed') throw new FilesApiError(HTTP_NOT_FOUND, 'notFound');
  return guarded(() => fetchBytes(ticket, hooks));
}

/**
 * The streamed read behind {@link fetchDownload}.
 *
 * The byte route answers **404 before any header** when the ticket is unknown, expired, or any
 * member of it has since been deleted — so a non-2xx here is the toast's Failed state and the cue
 * to request a fresh ticket, never something to retry against the same URL.
 */
async function fetchBytes(ticket: DownloadTicket, hooks: TransferHooks): Promise<Blob> {
  const response = await fetch(ticket.url, { signal: hooks.signal, credentials: 'omit' });
  if (!response.ok) {
    throw new FilesApiError(
      response.status,
      response.status === HTTP_NOT_FOUND ? 'notFound' : 'unknown',
    );
  }
  // `Content-Length` is exact on this route; the ticket's own size is the fallback for a body the
  // browser hands over without one, so the bar never divides by zero.
  const declared = Number(response.headers.get('Content-Length'));
  const total = Number.isFinite(declared) && declared > 0 ? declared : ticket.sizeBytes;

  const stream = response.body;
  if (stream === null) return new Blob([await response.arrayBuffer()], { type: ticket.mimeType });

  const reader = stream.getReader();
  const chunks: Uint8Array[] = [];
  let loaded = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    loaded += value.byteLength;
    hooks.onProgress?.(loaded, total);
  }
  return new Blob(chunks as BlobPart[], { type: ticket.mimeType });
}

/**
 * `DELETE /api/portal/me/files/{id}` — resolves on 204, rejects with an `errorCode` otherwise.
 *
 * Permanent: no archive, no recycle bin, no undo. Idempotent per file, so a repeat on an id that
 * is already gone answers 404 — which the caller folds into "deleted" rather than reporting.
 */
export async function deleteFile(id: string): Promise<void> {
  await guarded(() => deleteApiPortalMeFilesId(id));
}

/**
 * `POST /api/portal/me/files/delete` — **always 200**; the per-file outcomes are the answer.
 *
 * Not atomic by design, which is what the partial-result dialog is for. Results come back in the
 * order the ids were sent (or in list order for a `filter`), so pairing them positionally is safe.
 */
export async function deleteFiles(selection: FileSelectionDto): Promise<DeleteResult[]> {
  return toDeleteResultList(
    await guarded(() => postApiPortalMeFilesDelete(toSelectionRequest(selection))),
  );
}

/**
 * `POST /api/portal/me/files` — one file per request, multipart, with upload progress.
 *
 * `destination` must be `hawkins` today; anything else that is a real member answers
 * `409 providerNotConnected`. The caller offers no other choice (HS-1484), so the member travels
 * as the API requires rather than being assumed server-side.
 *
 * Progress and cancellation ride the axios request config rather than a wrapper: the generated
 * call forwards it to `customInstance`, which is the same instance every other request uses.
 */
export async function uploadFile(params: UploadParams, hooks: TransferHooks): Promise<FileRow> {
  const dto = await guarded(() =>
    postApiPortalMeFiles(
      // `folderId` absent puts the file at the root; the generated call omits an undefined part.
      { file: params.file, destination: params.destination, folderId: params.folderId },
      {
        signal: hooks.signal,
        headers: { [REQUESTED_WITH_HEADER]: REQUESTED_WITH_VALUE },
        onUploadProgress: (event) => {
          // Measured against the file, not the request. `event.loaded` counts the multipart
          // envelope — the boundary and the part headers — and `event.total` is absent for a
          // streamed body, so reporting those bytes against the file's own size drove the bar
          // past 100 %: a 596 B file showed "905 B of 596 B" at 152 %. The user asked to send a
          // file; the envelope is not theirs to watch.
          const total = params.file.size;
          hooks.onProgress?.(Math.min(event.loaded, total), total);
        },
      },
    ),
  );
  const item = toItemDto(dto);
  // A 201 without an id is not a file anything can act on; treated as the server error it is.
  if (item === null) throw new FilesApiError(HTTP_SERVER_ERROR, 'unknown');
  return toFileRow(item);
}

/** Whether a thrown value is the user's own cancellation rather than a failure. */
export function isCancellation(error: unknown): boolean {
  return error instanceof TransferCancelledError;
}

/** Whether a thrown value carries a `reason` the copy can be chosen from. */
export function isFilesApiError(error: unknown): error is FilesApiError {
  return error instanceof FilesApiError;
}

/** A 404 from a single-file route: the file is not (or no longer) this user's. */
export function isNotFound(error: unknown): boolean {
  return isFilesApiError(error) && error.status === HTTP_NOT_FOUND;
}
